import { memo, useCallback, useMemo, useState } from 'react';
import ReactPlayer from 'react-player';

import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import GetAppIcon from '@material-ui/icons/GetApp';

import { PauseIcon, PlayIcon, colors } from '../../assets';
import { useAlert } from '../../hooks';
import { downloadFile } from '../../utils';
import { Loader } from '../loader/Loader';
import { CustomSlider, useStyles } from './AudioPlayer.styles';
import { AudioPlayerProps } from './AudioPlayer.types';
import { Duration } from './duration/Duration';
import { usePlayer } from './usePlayerControls';

export const AudioPlayer = memo(({ url, downloadUrl }: AudioPlayerProps) => {
  const styles = useStyles();

  const player = usePlayer();

  const [isDownloading, setIsDownloading] = useState(false);

  const { showAlert } = useAlert();

  const isDisabled = useMemo(() => !player.state.ready || player.state.error, [player.state.ready, player.state.error]);

  const handleDownload = useCallback(async () => {
    if (!downloadUrl) {
      return;
    }

    const fileName = downloadUrl.split('/').pop() || 'recording';

    setIsDownloading(true);
    try {
      await downloadFile(downloadUrl, fileName, true);
    } catch {
      showAlert('Error! Failed to download audio.', { variant: 'error' });
    }
    setIsDownloading(false);
  }, [downloadUrl, showAlert]);

  return (
    <div>
      <div className={styles.playButtonContainer}>
        <IconButton disabled={isDisabled} className={styles.playButton} onClick={player.controls.handlePlayPause}>
          {player.state.playing ? (
            <PauseIcon fontSize={40} color={isDisabled ? colors.grey60 : colors.primary.main} />
          ) : (
            <PlayIcon fontSize={40} color={isDisabled ? colors.grey60 : colors.primary.main} />
          )}
        </IconButton>

        {downloadUrl && (
          <div className={styles.downloadButton}>
            {isDownloading ? (
              <Loader size="small" />
            ) : (
              <IconButton size="medium" className="iconButtonMedium" onClick={handleDownload}>
                <GetAppIcon />
              </IconButton>
            )}
          </div>
        )}
      </div>

      <div className={styles.durationContainer}>
        <CustomSlider
          min={0}
          max={0.999999}
          step={0.999999 / player.state.duration}
          value={player.state.played}
          disabled={isDisabled}
          onChange={(_, newVal) => {
            player.state.setPlayed(newVal as number);
          }}
          onChangeCommitted={(_, newVal) => {
            player.state.player?.seekTo(newVal as number);
          }}
        />

        <div className={styles.timeContainer}>
          <Typography variant="caption">
            <Duration seconds={player.state.duration * player.state.played} />
          </Typography>

          <Typography variant="caption">
            <Duration seconds={player.state.duration} />
          </Typography>
        </div>
      </div>

      <ReactPlayer
        ref={player.controls.ref}
        width={0}
        height={0}
        url={url}
        playing={player.state.playing}
        onPlay={player.controls.handlePlay}
        onPause={player.controls.handlePause}
        onProgress={player.controls.handleProgress}
        onDuration={player.controls.handleDuration}
        onReady={player.controls.handleReady}
        onError={player.controls.handleError}
      />
    </div>
  );
});
